package fyi.moko.hotpl.domain.model

/**
 * Which letter a place name files under in the A–Z index, and the key that puts names in
 * that order.
 *
 * ── 왜 표를 직접 들고 있는가 ──────────────────────────────
 * `java.text.Normalizer`(NFD로 분해해 결합 문자를 떼는 표준 방법)는 JVM에만 있다. 이 코드는
 * commonMain이라 Android·iOS·JS가 같이 쓰므로, **자주 나오는 라틴 확장 문자만 손으로 접는다.**
 * 실제로 이미 걸리는 이름이 있다 — `Rōk Coffee and Tea`의 `ō`를 안 접으면 R이 아니라 [OTHER]로
 * 떨어진다.
 *
 * 표에 없는 문자(한글·한자·키릴…)는 [OTHER]로 모인다. 그것이 틀린 답은 아니다 — 지금 이 앱의
 * 이름은 사실상 전부 라틴 문자이고(LA 한인타운 가게도 영문 표기로 들어와 있다), 한글 이름이
 * 늘어나면 **이 함수 하나만 고쳐** ㄱ~ㅎ를 앞에 붙이면 된다.
 */
const val OTHER_BUCKET = '#'

/**
 * The index letter for [name] — `A`–`Z`, or [OTHER_BUCKET] for anything that doesn't start
 * with a Latin letter (digits, symbols, other scripts, or an empty name).
 */
fun alphabetBucket(name: String): Char {
    val first = name.trim().firstOrNull() ?: return OTHER_BUCKET
    val folded = first.foldedToAscii().uppercaseChar()
    return if (folded in 'A'..'Z') folded else OTHER_BUCKET
}

/**
 * Sort key for the name index: folded to plain ASCII letters and lowercased, so `Rōk` sits
 * between `Rise` and `Roost` rather than after `Zuma`.
 *
 * 관사는 **떼지 않는다**. `The Coffee Shop`은 T에 들어간다 — 정렬 자리와 화면에 보이는 첫
 * 글자가 어긋나면 색인을 따라간 사용자가 자기 눈을 의심하게 된다.
 */
fun placeNameSortKey(name: String): String =
    name.trim().map { it.foldedToAscii() }.joinToString("").lowercase()

/**
 * Strips the accent from the Latin letters that actually turn up in place names. Anything
 * else comes back unchanged.
 */
private fun Char.foldedToAscii(): Char = when (this) {
    'À', 'Á', 'Â', 'Ã', 'Ä', 'Å', 'Ā', 'Ă', 'Ą' -> 'A'
    'à', 'á', 'â', 'ã', 'ä', 'å', 'ā', 'ă', 'ą' -> 'a'
    'Ç', 'Ć', 'Č' -> 'C'
    'ç', 'ć', 'č' -> 'c'
    'Ď', 'Đ' -> 'D'
    'ď', 'đ' -> 'd'
    'È', 'É', 'Ê', 'Ë', 'Ē', 'Ĕ', 'Ė', 'Ę', 'Ě' -> 'E'
    'è', 'é', 'ê', 'ë', 'ē', 'ĕ', 'ė', 'ę', 'ě' -> 'e'
    'Ì', 'Í', 'Î', 'Ï', 'Ī', 'Į' -> 'I'
    'ì', 'í', 'î', 'ï', 'ī', 'į', 'ı' -> 'i'
    'Ł' -> 'L'
    'ł' -> 'l'
    'Ñ', 'Ń', 'Ň' -> 'N'
    'ñ', 'ń', 'ň' -> 'n'
    'Ò', 'Ó', 'Ô', 'Õ', 'Ö', 'Ø', 'Ō', 'Ŏ', 'Ő' -> 'O'
    'ò', 'ó', 'ô', 'õ', 'ö', 'ø', 'ō', 'ŏ', 'ő' -> 'o'
    'Ř' -> 'R'
    'ř' -> 'r'
    'Ś', 'Š', 'Ş' -> 'S'
    'ś', 'š', 'ş' -> 's'
    'Ť', 'Ţ' -> 'T'
    'ť', 'ţ' -> 't'
    'Ù', 'Ú', 'Û', 'Ü', 'Ū', 'Ŭ', 'Ů', 'Ű', 'Ų' -> 'U'
    'ù', 'ú', 'û', 'ü', 'ū', 'ŭ', 'ů', 'ű', 'ų' -> 'u'
    'Ý', 'Ÿ' -> 'Y'
    'ý', 'ÿ' -> 'y'
    'Ź', 'Ż', 'Ž' -> 'Z'
    'ź', 'ż', 'ž' -> 'z'
    else -> this
}
